import { type ReactNode, useState } from "react";
import { type SelectChangeEvent, Box, type SxProps, type Theme } from "~/common/mui";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";

import type { MediaItemDetails } from "../../types/mediaLibraryTypes";
import { fetchMediaItemDetails } from "~/features/mediaLibrary/api/fetchMediaItemDetails";
import { addToFavourites } from "~/features/mediaLibrary/api/addToFavourites";
import { userIsPublic } from "~/domains/user/utils/roles";
import { ImageWithFallback } from "../MediaLibraryItem/MediaLibraryItem";
import { MediaType } from "../../constants";
import { VideoPreview } from "./VideoPreview";
import { PdfPreview } from "./PdfPreview";
import { trackAssetInteraction, trackShareAsset } from "~/app/analytics/ga/mediaLibrary";

const previewImageSx: SxProps<Theme> = {
  width: 1,
  height: 1,
  objectFit: "contain",
};

const previewTwoImageSx: SxProps<Theme> = {
  width: 1,
  height: "auto",
  maxHeight: "50vh",
  objectFit: "contain",
};

const previewIconContainerSx: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

const previewFileIconSx: SxProps<Theme> = {
  color: "text.label",
  width: "128px !important",
  height: "128px !important",
};

type PreviewRenderer = {
  render: (mediaItem: MediaItemDetails) => ReactNode;
  extension?: (mediaItem: MediaItemDetails) => string;
};

const getFileExtension = (mediaType: string) => {
  const extension = mediaType?.split("/")?.[1];
  return extension ? `.${extension}` : "";
};

export { previewImageSx, previewTwoImageSx };

export const PREVIEWS: Record<MediaType, PreviewRenderer> = {
  [MediaType.IMAGE]: {
    render: ({ previewUrl, mediaType }) => (
      <ImageWithFallback previewUrl={previewUrl} mediaType={mediaType} previewImageSx={previewImageSx} />
    ),
    extension: ({ mediaType }) => getFileExtension(mediaType),
  },
  [MediaType.VIDEO]: {
    render: (mediaItem) => <VideoPreview mediaItem={mediaItem} />,
    extension: ({ mediaType }) => getFileExtension(mediaType),
  },

  [MediaType.PDF]: {
    render: ({ documentPages }) => <PdfPreview pages={documentPages} />,
    extension: ({ mediaType }) => getFileExtension(mediaType),
  },

  [MediaType.ZIP]: {
    render: () => (
      <Box sx={previewIconContainerSx}>
        <FolderZipOutlinedIcon sx={previewFileIconSx} />
      </Box>
    ),
    extension: () => ".zip",
  },

  [MediaType.SHEET]: {
    render: () => (
      <Box sx={previewIconContainerSx}>
        <FeedOutlinedIcon sx={previewFileIconSx} />
      </Box>
    ),
    extension: () => ".xlsx",
  },

  [MediaType.WORD]: {
    render: () => (
      <Box sx={previewIconContainerSx}>
        <ArticleOutlinedIcon sx={previewFileIconSx} />
      </Box>
    ),
    extension: () => ".docx",
  },

  [MediaType.PPT]: {
    render: () => (
      <Box sx={previewIconContainerSx}>
        <BrokenImageOutlinedIcon sx={previewFileIconSx} />
      </Box>
    ),
    extension: () => ".docx",
  },

  [MediaType.UNKNOWN]: {
    render: () => (
      <Box sx={previewIconContainerSx}>
        <BrokenImageOutlinedIcon sx={previewFileIconSx} />
      </Box>
    ),
    extension: ({ mediaType }) => mediaType,
  },
};

export const useMediaPreview = ({
  id,
  groupId,
  updateMediaItems,
  updateSelectedMediaItem,
  openLoginPopup,
}: {
  id: string;
  groupId: string;
  updateMediaItems: (id: string, updatedFav: boolean) => void;
  updateSelectedMediaItem: (mediaDetails: MediaItemDetails) => void;
  openLoginPopup: () => void;
}) => {
  const [mediaItemApiResponse, setMediaItemApiResponse] = useState<MediaItemDetails[]>([]);
  const [mediaItem, setMediaItem] = useState<MediaItemDetails>();
  const [languages, setLanguages] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");

  const fetchMediaItem = async () => {
    const response = await fetchMediaItemDetails(groupId);
    setMediaItemApiResponse(response);

    if (!response || response.length === 0) {
      return;
    }
    if (response.length === 1) {
      setMediaItem(response[0]);
    } else {
      const result = response.find((item) => item.id === id);
      if (result) {
        setMediaItem(result);
        if (result.language) {
          setSelectedLanguage(result.language);
        }
      }

      const languageArray = [...new Set(response.map((item) => item.language).filter(Boolean))];
      setLanguages(languageArray);
    }
  };

  const handleChangeLanguage = (e: SelectChangeEvent<string>) => {
    const selectedLang = e.target.value;
    const mediaItemDetails = mediaItemApiResponse.find((item) => item.language === selectedLang);
    setSelectedLanguage(selectedLang);
    if (mediaItemDetails) {
      setMediaItem(mediaItemDetails);
      updateSelectedMediaItem(mediaItemDetails);
    }
  };

  const handleClickFavourite = async () => {
    if (userIsPublic()) {
      openLoginPopup();
      window.sessionStorage.setItem("favAssetId", id);
      window.sessionStorage.setItem("isPreviewOpen", "true");
      return;
    }
    if (!mediaItem) {
      return;
    }
    const updateFavouriteValue = !mediaItem.isFavourite;
    const status = await addToFavourites({ assetID: id, addToFavourite: updateFavouriteValue });
    if (status === 200) {
      trackAssetInteraction(
        updateFavouriteValue ? "Bookmark Added" : "Bookmark Removed",
        id,
        mediaItem?.title,
        mediaItem?.language,
        mediaItem?.mediaType,
      );
      setMediaItem({ ...mediaItem, isFavourite: updateFavouriteValue });
      updateMediaItems(id, updateFavouriteValue);
    }
  };

  const trackOnShare = () => {
    trackAssetInteraction("Share Options Accessed", id, mediaItem?.title, mediaItem?.language, mediaItem?.mediaType);
  };

  const trackOnShareWith = (shareWith: string) => {
    trackShareAsset(shareWith, id, mediaItem?.title, mediaItem?.language, mediaItem?.mediaType);
  };

  return {
    PREVIEWS,
    mediaItem,
    languages,
    selectedLanguage,
    fetchMediaItem,
    handleChangeLanguage,
    handleClickFavourite,
    trackOnShare,
    trackOnShareWith,
  };
};
